import { useCallback, useEffect, useState } from 'react';

export const useIsScrollable = (element: HTMLElement | null) => {
  const [isScrollable, setScrollable] = useState<boolean | undefined>(undefined);
  const [isMaxLeft, setMaxLeft] = useState<undefined | boolean>(undefined);
  const [isMaxRight, setMaxRight] = useState<undefined | boolean>(undefined);

  const handleWindowResize = useCallback(() => {
    if (element) {
      setScrollable(element.scrollWidth > element.clientWidth);
    }
  }, [element]);

  const handleElementScroll = useCallback(() => {
    if (element) {
      const { scrollLeft, scrollWidth, clientWidth } = element;

      if (scrollLeft === 0) {
        setMaxLeft(true);
      } else {
        setMaxLeft(false);
      }

      if (scrollLeft + clientWidth === scrollWidth) {
        setMaxRight(true);
      } else {
        setMaxRight(false);
      }
    }
  }, [element]);

  useEffect(() => {
    if (element) {
      setScrollable(element.scrollWidth > element.clientWidth);
    }
  }, [element]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);

    return () => window.removeEventListener('resize', handleWindowResize);
  }, [handleWindowResize]);

  useEffect(() => {
    if (isScrollable) {
      handleElementScroll();

      if (element) {
        element.addEventListener('scroll', handleElementScroll);
      }

      return () => element?.removeEventListener('scroll', handleElementScroll);
    }

    if (!isScrollable) {
      setMaxLeft(undefined);
      setMaxRight(undefined);
    }
  }, [element, handleElementScroll, isScrollable]);

  const scrollLeft = useCallback(() => {
    if (isScrollable && !isMaxLeft && element) {
      element.scrollBy({ left: (element.clientWidth / 2) * -1, behavior: 'smooth' });
    }
  }, [element, isMaxLeft, isScrollable]);

  const scrollRight = useCallback(() => {
    if (isScrollable && !isMaxRight && element) {
      element.scrollBy({ left: element.clientWidth / 2, behavior: 'smooth' });
    }
  }, [element, isMaxRight, isScrollable]);

  return { isScrollable, isMaxLeft, isMaxRight, scrollLeft, scrollRight };
};
